/*
 * useClusterMetrics — single source of truth for cluster-level cost,
 * utilization, and efficiency numbers across Dashboard, Monitoring,
 * Monitoring's Cost Breakdown tab, and any other consumer.
 *
 * Why this exists: those pages previously hit different backend endpoints
 * (/realtime-metrics, /cost-projection, /dashboard/stats, /clusters/{id}/shadow-cost)
 * for the same conceptual metric, so "Daily Cost" on Dashboard and "Daily
 * Cost" on Monitoring would disagree, and "Efficiency" meant cost-waste %
 * on one page and CPU+mem utilization % on another.
 *
 * Convention enforced by this hook:
 *   - Live snapshot values (cost_per_hour, daily projection, cpu%, mem%,
 *     efficiency) come from /realtime-metrics. These are "right now".
 *   - Month-to-date integrated values (mtdSpend, mtdSavings, mtdShadow)
 *     come from /clusters/{cid}/shadow-cost. These are "since the 1st".
 *   - Efficiency is utilization-based (cpu+mem)/2. This matches operator
 *     intuition; the older cost-based "optimization rate" lives on as
 *     mtdSavingsPct here for the same role with a clearer name.
 *
 * Polls every 30s; same cadence as MonitoringContext's realtime poll so
 * adopting this hook doesn't change network behavior.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../App';

const POLL_MS = 30_000;

const EMPTY = {
  // Live values
  costPerHour: 0,
  costPerMinute: 0,
  costPerSecond: 0,
  dailyCost: 0,
  monthlyProjection: 0,
  cpuUsage: 0,
  memoryUsage: 0,
  efficiency: 0,
  podsCount: 0,
  nodesCount: 0,
  // Month-to-date integrated (from shadow_snapshots)
  mtdSpend: 0,
  mtdSavings: 0,
  mtdShadowCost: 0,
  mtdSavingsPct: 0,
  // Meta
  loading: true,
  error: null,
  lastUpdated: null,
};

export function useClusterMetrics(clusterId) {
  const [data, setData] = useState(EMPTY);
  const mountedRef = useRef(true);
  const lastValidRef = useRef(null); // preserve numbers on transient errors

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!clusterId) {
      setData(EMPTY);
      return;
    }

    const fetchOnce = async () => {
      try {
        const mtdDays = new Date().getDate(); // 1..31
        const [rtRes, shadowRes] = await Promise.all([
          axios.get(`${API}/realtime-metrics/${clusterId}`).catch(() => ({ data: null })),
          axios.get(`${API}/clusters/${clusterId}/shadow-cost?days=${mtdDays}`).catch(() => ({ data: null })),
        ]);
        const rt = rtRes.data || {};
        const shadow = shadowRes.data || {};
        const summary = shadow.summary || {};
        const eff = rt.efficiency_metrics || {};

        const costPerHour = Number(rt.cost_per_hour ?? 0);
        const cpu = Number(rt.cpu_usage ?? 0);
        const mem = Number(rt.memory_usage ?? 0);
        const overallEff = Number(
          eff.overall_efficiency ?? ((cpu + mem) / 2)
        );

        const next = {
          costPerHour,
          costPerMinute: Number(rt.cost_per_minute ?? costPerHour / 60),
          costPerSecond: Number(rt.cost_per_second ?? costPerHour / 3600),
          dailyCost: costPerHour * 24,
          monthlyProjection: costPerHour * 24 * 30,
          cpuUsage: cpu,
          memoryUsage: mem,
          efficiency: overallEff,
          podsCount: Number(rt.pods_count ?? 0),
          nodesCount: Number(rt.nodes_count ?? 0),
          mtdSpend: Number(summary.total_actual ?? 0),
          mtdSavings: Number(summary.total_savings ?? 0),
          mtdShadowCost: Number(summary.total_shadow ?? 0),
          mtdSavingsPct: Number(summary.avg_savings_pct ?? 0),
          loading: false,
          error: null,
          lastUpdated: new Date(),
        };

        lastValidRef.current = next;
        if (mountedRef.current) setData(next);
      } catch (e) {
        if (!mountedRef.current) return;
        // Preserve last-good numbers; surface the error in `error`.
        setData((prev) => ({
          ...(lastValidRef.current || prev),
          loading: false,
          error: e?.response?.data?.detail || e.message || 'fetch failed',
        }));
      }
    };

    fetchOnce();
    const t = setInterval(fetchOnce, POLL_MS);
    return () => clearInterval(t);
  }, [clusterId]);

  return data;
}
